// ===========================================
// Payment Options Modal
// ===========================================
// Modal dialog for configuring payment frequency options

import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  FormControlLabel,
  Switch,
  Alert,
  Typography,
  Skeleton,
  Divider,
} from '@mui/material';
import { Payment as PaymentIcon } from '@mui/icons-material';
import FormModal from '../../common/FormModal';
import { usePaymentFrequencies, useUpdatePaymentFrequencies } from '../../../hooks/usePricing';

// ===========================================
// TYPES
// ===========================================

interface PaymentOptionsModalProps {
  open: boolean;
  onClose: () => void;
}

interface ToggleItemProps {
  checked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  title: string;
  description: string;
  disabled?: boolean;
}

interface ToggleSectionProps {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

// ===========================================
// SUB-COMPONENTS
// ===========================================

/**
 * Reusable toggle item with title and description
 */
function ToggleItem({ checked, onChange, title, description, disabled = false }: ToggleItemProps) {
  return (
    <Box>
      <FormControlLabel
        control={
          <Switch
            checked={checked}
            onChange={onChange}
            disabled={disabled}
          />
        }
        label={
          <Box>
            <Typography
              variant="body1"
              fontWeight={500}
              color={disabled ? 'text.disabled' : 'text.primary'}
            >
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
        }
        sx={{ alignItems: 'flex-start', ml: 0 }}
      />
    </Box>
  );
}

/**
 * Reusable section container with title and optional description
 */
function ToggleSection({ title, description, icon, children }: ToggleSectionProps) {
  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: description ? 2 : 3 }}>
        {icon}
        <Typography variant="h6">{title}</Typography>
      </Box>

      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {description}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {children}
      </Box>
    </Paper>
  );
}

// ===========================================
// TOGGLE CONFIGURATIONS
// ===========================================

const frequencyToggles = [
  {
    field: 'term' as const,
    title: 'Per Term',
    description: 'Pay for the full term upfront',
  },
  {
    field: 'month' as const,
    title: 'Per Month',
    description: 'Break the term fee into monthly payments',
  },
  {
    field: 'week' as const,
    title: 'Per Week',
    description: 'Pay weekly installments throughout the term',
  },
];

const subscriptionToggles = [
  {
    field: 'termSubscriptionEnabled' as const,
    baseField: 'term' as const,
    title: 'Term Subscription',
    description: 'Auto-charge at the start of each new term',
  },
  {
    field: 'monthSubscriptionEnabled' as const,
    baseField: 'month' as const,
    title: 'Monthly Subscription',
    description: 'Auto-charge on the same day each month',
  },
  {
    field: 'weekSubscriptionEnabled' as const,
    baseField: 'week' as const,
    title: 'Weekly Subscription',
    description: 'Auto-charge on the same day each week',
  },
];

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function PaymentOptionsModal({ open, onClose }: PaymentOptionsModalProps) {
  const { data: frequencyData, isLoading, error } = usePaymentFrequencies();
  const updateMutation = useUpdatePaymentFrequencies();

  const [formData, setFormData] = useState({
    term: true,
    month: false,
    week: false,
    termSubscriptionEnabled: false,
    monthSubscriptionEnabled: false,
    weekSubscriptionEnabled: false,
  });

  // Sync form data when modal opens or data changes
  useEffect(() => {
    if (open && frequencyData) {
      setFormData({
        term: frequencyData.term,
        month: frequencyData.month,
        week: frequencyData.week,
        termSubscriptionEnabled: frequencyData.termSubscriptionEnabled,
        monthSubscriptionEnabled: frequencyData.monthSubscriptionEnabled,
        weekSubscriptionEnabled: frequencyData.weekSubscriptionEnabled,
      });
    }
  }, [open, frequencyData]);

  // Validation: at least one frequency required
  const isValid = formData.term || formData.month || formData.week;

  // Handle toggle change with auto-disable subscription logic
  const handleToggle = (field: keyof typeof formData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setFormData((prev) => {
      const updated = { ...prev, [field]: newValue };
      // Auto-disable subscription if base frequency is disabled
      if (field === 'term' && !newValue) updated.termSubscriptionEnabled = false;
      if (field === 'month' && !newValue) updated.monthSubscriptionEnabled = false;
      if (field === 'week' && !newValue) updated.weekSubscriptionEnabled = false;
      return updated;
    });
  };

  // Handle save
  const handleSubmit = async () => {
    if (!isValid) return;
    try {
      await updateMutation.mutateAsync(formData);
      onClose();
    } catch {
      // Error handled by mutation
    }
  };

  // Loading state content
  const loadingContent = (
    <Box>
      <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
      <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1, mt: 2 }} />
    </Box>
  );

  // Error state content
  const errorContent = (
    <Alert severity="error">
      Failed to load payment options. Please try again.
    </Alert>
  );

  // Main content using extracted components
  const mainContent = (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure which payment frequency options are available to parents. At least one option
        must be enabled.
      </Typography>

      {/* Payment Frequencies Section */}
      <ToggleSection
        title="Payment Frequencies"
        icon={<PaymentIcon color="primary" />}
      >
        {frequencyToggles.map((toggle, index) => (
          <Box key={toggle.field}>
            <ToggleItem
              checked={formData[toggle.field]}
              onChange={handleToggle(toggle.field)}
              title={toggle.title}
              description={toggle.description}
            />
            {index < frequencyToggles.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Box>
        ))}
      </ToggleSection>

      {/* Subscription Options Section */}
      <Box sx={{ mt: 3 }}>
        <ToggleSection
          title="Subscription Options"
          description="When enabled, parents can opt-in to automatic recurring payments. Their card will be charged automatically at each billing period."
        >
          {subscriptionToggles.map((toggle, index) => (
            <Box key={toggle.field}>
              <ToggleItem
                checked={formData[toggle.field]}
                onChange={handleToggle(toggle.field)}
                title={toggle.title}
                description={toggle.description}
                disabled={!formData[toggle.baseField]}
              />
              {index < subscriptionToggles.length - 1 && <Divider sx={{ mt: 2 }} />}
            </Box>
          ))}
        </ToggleSection>
      </Box>

      {/* Validation warning */}
      {!isValid && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          At least one payment frequency must be enabled.
        </Alert>
      )}

      {/* Error alert */}
      {updateMutation.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to save payment options. Please try again.
        </Alert>
      )}
    </Box>
  );

  return (
    <FormModal
      open={open}
      title="Payment Options"
      submitLabel="Save"
      loading={updateMutation.isPending}
      maxWidth="sm"
      onSubmit={handleSubmit}
      onClose={onClose}
    >
      {isLoading ? loadingContent : error ? errorContent : mainContent}
    </FormModal>
  );
}
